/* Created by Interactive 63 - https://interactive63.com */
/**
 * Dragging an artboard handle on the stage. The pointer's movement is turned into artwork units,
 * and the frame is resized around the opposite side. Shift keeps the proportions on a corner and
 * Alt resizes from the centre. The whole drag is one undo step.
 */
import { dragHandle, type ArtboardHandle } from '../../../core/model/artboard';
import { setArtboard } from '../../store/artboard-actions';
import type { Store } from '../../store/store';
import type { OverlayScene } from './overlay';

export interface ArtboardDragServices {
  store: Store;
  setReadout(readout: OverlayScene['readout']): void;
}

const READOUT_OFFSET = 14;
let dragCounter = 0;

/** Starts resizing the artboard from a handle, following the pointer until it is released. */
export function beginArtboardDrag(
  services: ArtboardDragServices,
  handle: ArtboardHandle,
  down: PointerEvent,
): void {
  const { store } = services;
  const { project, editor } = store.getState();
  const start = project.svg.viewBox;
  const zoom = editor.stageZoom;
  dragCounter += 1;
  const key = `drag:artboard:${dragCounter}`;

  /** Resizes the frame for the pointer's current position. */
  const move = (event: PointerEvent): void => {
    const dx = (event.clientX - down.clientX) / zoom;
    const dy = (event.clientY - down.clientY) / zoom;
    const box = dragHandle(start, handle, dx, dy, {
      fromCentre: event.altKey,
      keepRatio: event.shiftKey,
    });
    setArtboard(store, box, key);
    services.setReadout({
      text: `${box.width} × ${box.height}`,
      x: event.clientX + READOUT_OFFSET,
      y: event.clientY + READOUT_OFFSET,
    });
  };
  /** Stops following the pointer. */
  const end = (): void => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', end);
    services.setReadout(null);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', end);
}
